<template>
  <vs-popup title="Identificar Encerramento em Massa" :active.sync="isActive" @close="close()">
    <div class="d-flex flex-column" style="gap:16px; padding:10px;">
      <p class="step-description">Escolha o período em que os encerramentos foram realizados.</p>

      <div class="d-flex" style="align-items:center; gap:16px;">
        <div style="flex:1">
          <flat-pickr
            :config="datetimepickr"
            v-model="startAt"
            name="Período (de)"
            placeholder="Data e hora inicial"
            v-validate="'required'"
            style="width:100%; border-radius:6px; padding:8px 10px;"
          />
        </div>

        <div style="flex:1">
          <flat-pickr
            :config="finishAtConfig(datetimepickr)"
            v-model="finishAt"
            name="Período (até)"
            placeholder="Data e hora final"
            v-validate="'required'"
            :disabled="startAt === ''"
            style="width:100%; border-radius:6px; padding:8px 10px;"
          />
        </div>
      </div>

      <div v-if="loading" class="d-flex justify-center" style="padding: 20px;">
        <vs-progress indeterminate color="primary">Buscando dados...</vs-progress>
      </div>

      <div v-if="rankResult && rankResult.length > 0 && !loading" class="rank-result">
        <h4>Ranking de Encerramentos</h4>
        <div class="rank-table">
          <div class="rank-header">
            <div class="rank-col">Posição</div>
            <div class="rank-col">Usuário</div>
            <div class="rank-col">Total de Encerramentos</div>
          </div>
          <div 
            v-for="(item, index) in rankResult" 
            :key="item.user_id"
            class="rank-row"
            :class="{'highlight': index === 0}"
          >
            <div class="rank-col">{{ index + 1 }}º</div>
            <div class="rank-col">{{ item.user_name }}</div>
            <div class="rank-col">{{ item.total_finished }}</div>
          </div>
        </div>
      </div>

      <div v-if="rankResult && rankResult.length === 0 && !loading" class="no-data">
        <p>Nenhum encerramento encontrado no período selecionado.</p>
      </div>

      <div class="d-flex justify-end" v-if="!loading && !rankResult">
        <vs-button type="filled" @click="searchRank" :disabled="disableSearch">
          Buscar
        </vs-button>
      </div>

      <div class="d-flex justify-end" v-if="rankResult && !loading">
        <vs-button type="border" @click="clearResults">
          Nova Busca
        </vs-button>
      </div>
    </div>
  </vs-popup>
</template>

<script>
import {mapGetters} from 'vuex';
import 'flatpickr/dist/flatpickr.css';
import flatPickr from 'vue-flatpickr-component';
import {Portuguese} from 'flatpickr/dist/l10n/pt.js';

export default {
  name: 'FinishedTicketsRank',
  components: {flatPickr},
  props: {
    isActive: {
      type: Boolean,
      default: false
    },
    tenantName: {
      type: String,
      required: true
    }
  },
  computed: {
    ...mapGetters(['token']),
    disableSearch(){
      return !this.startAt || !this.finishAt || this.startAt === '' || this.finishAt === ''
    }
  },
  data() {
    return {
      startAt: '',
      finishAt: '',
      rankResult: null,
      loading: false,
      datetimepickr: {
        enableTime: true,
        noCalendar: false,
        dateFormat: "Y-m-d H:i:S",
        time_24hr: true,
        locale: Portuguese
      }
    }
  },
  methods: {
    close() {
      this.startAt = '';
      this.finishAt = '';
      this.rankResult = null;
      this.loading = false;
      this.$emit('isActiveFalse');
    },
    finishAtConfig(config) {
      return {
        ...config,
        minDate: this.startAt
      }
    },
    clearResults() {
      this.rankResult = null;
      this.startAt = '';
      this.finishAt = '';
    },
    searchRank() {
      this.loading = true;
      this.rankResult = null;

      this.$store.dispatch('getFinishedTicketsRank', {
        from: this.startAt,
        to: this.finishAt,
        tenantName: this.tenantName,
        token: this.token
      }).then(response => {
        this.rankResult = response.data || [];
        this.loading = false;
      }).catch(exception => {
        console.error(exception);
        this.loading = false;
        this.$vs.notify({
          color: 'danger',
          title: 'Erro!',
          text: 'Ocorreu um erro ao buscar os dados! Entre em contato com o time técnico.'
        });
      });
    },
  },
  watch: {
    isActive(value) {
      if (!value) {
        this.startAt = '';
        this.finishAt = '';
        this.rankResult = null;
        this.loading = false;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.d-flex {
  display: flex;
}

.flex-column {
  flex-direction: column;
}

.justify-end {
  justify-content: flex-end;
}

.justify-center {
  justify-content: center;
}

.step-content {
  h4 {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin-bottom: 8px;
  }

  .step-description {
    font-size: 13px;
    color: #666;
    margin-bottom: 20px;
    line-height: 1.4;
  }
}

.rank-result {
  background: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
  margin-top: 16px;

  h4 {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin-bottom: 12px;
    border-bottom: 2px solid #ddd;
    padding-bottom: 8px;
  }
}

.rank-table {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.rank-header {
  display: flex;
  background: #ddd;
  border-radius: 4px;
  padding: 8px;
  font-weight: 600;
  font-size: 12px;
  color: #333;
}

.rank-row {
  display: flex;
  background: #fff;
  border-radius: 4px;
  padding: 8px;
  font-size: 13px;
  color: #666;
  transition: background 0.2s;

  &:hover {
    background: #f9f9f9;
  }

  &.highlight {
    background: #ffe0e0;
    font-weight: 600;

    &:hover {
      background: #ffd0d0;
    }
  }
}

.rank-col {
  flex: 1;
  padding: 0 8px;

  &:first-child {
    flex: 0 0 80px;
    text-align: center;
  }

  &:last-child {
    flex: 0 0 180px;
    text-align: center;
  }
}

.no-data {
  text-align: center;
  padding: 20px;
  color: #666;
  font-style: italic;
}
</style>
